import { MapPin, Star, Phone, Globe, Clock, DollarSign } from "lucide-react";
import type { ToolWidgetProps} from "./index";

interface Category {
  id: number;
  name: string;
  icon?: string;
}

interface Location {
  formatted_address?: string;
  locality?: string;
  region?: string;
  country?: string;
}

interface Hours {
  display?: string;
  open_now?: boolean;
  regular?: Array<{ day: number; open: string; close: string }>;
}

interface Place {
  fsq_place_id: string;
  name: string;
  description?: string;
  categories?: Category[];
  location?: Location;
  latitude?: number;
  longitude?: number;
  rating?: number;
  price?: number;
  phone?: string;
  email?: string;
  website?: string;
  hours?: Hours;
  popularity?: number;
}

interface FoursquareDetailsData {
  results: Place[];
}

function PriceLevel({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(4)].map((_, i) => (
        <DollarSign
          key={i}
          className={`w-3 h-3 ${i < level ? "text-green-600" : "text-slate-300"}`}
        />
      ))}
    </div>
  );
}

export function FoursquareDetailsWidget({
  data,
}: ToolWidgetProps<FoursquareDetailsData>) {
  const { results = [] } = data || {};
  const place = results[0];

  if (!place) {
    return (
      <div className="text-sm text-slate-500 italic">Aucune information trouvée</div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-3">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-800">{place.name}</h3>
          {place.rating && (
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="font-medium text-slate-700">
                {place.rating / 10}
              </span>
            </div>
          )}
        </div>
        {place.categories && place.categories.length > 0 && (
          <div className="text-sm text-slate-500 mt-1">
            {place.categories.map((c) => c.name).join(", ")}
          </div>
        )}
      </div>

      {/* Description */}
      {place.description && (
        <p className="text-sm text-slate-600">{place.description}</p>
      )}

      {/* Details Grid */}
      <div className="space-y-2 text-sm">
        {place.location?.formatted_address && (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-slate-700">{place.location.formatted_address}</span>
          </div>
        )}

        {place.hours && (
          <div className="flex items-start gap-2">
            <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-slate-700">{place.hours.display || "Horaires non disponibles"}</span>
              {place.hours.open_now !== undefined && (
                <span
                  className={`ml-2 text-xs px-2 py-0.5 rounded ${
                    place.hours.open_now
                      ? "bg-green-100 text-green-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {place.hours.open_now ? "Ouvert" : "Fermé"}
                </span>
              )}
            </div>
          </div>
        )}

        {place.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-slate-400" />
            <a
              href={`tel:${place.phone}`}
              className="text-default-600 hover:text-default-700"
            >
              {place.phone}
            </a>
          </div>
        )}

        {place.website && (
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-400" />
            <a
              href={place.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-default-600 hover:text-default-700 truncate"
            >
              {place.website}
            </a>
          </div>
        )}

        {place.price && (
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Prix</span>
            <PriceLevel level={place.price} />
          </div>
        )}
      </div>
    </div>
  );
}
